import { useTranslation } from 'react-i18next';
import { useNavigate } from 'react-router-dom';
import { Check, User } from '@/components/icons';
import { Avatar, AvatarFallback, AvatarImage } from '@/components/ui/avatar';
import { Button } from '@/components/ui/button';
import {
  DropdownMenu,
  DropdownMenuContent,
  DropdownMenuItem,
  DropdownMenuLabel,
  DropdownMenuSeparator,
  DropdownMenuTrigger,
} from '@/components/ui/dropdown-menu';
import { useAuth } from '@/auth/AuthProvider';
import { useActivePartner } from '@/lib/api/partners';
import { useSwitchOrg } from '@/hooks/useSwitchOrg';
import { cn } from '@/lib/utils';

/**
 * HARMONIZATION.md § 6.4 row "User-menu row" + BRAND.md § 3.6 token
 * discipline — chrome menu items use the canonical ink-N palette
 * directly (NOT the shadcn `accent`/`muted`/`foreground` aliases, which
 * resolve to app-local overrides a reviewer can't see without opening
 * `tailwind.config`). Each DropdownMenuItem carries this override to push
 * past the primitive's `focus:bg-accent focus:text-accent-foreground`
 * defaults.
 *
 * Default + hover/focus pair (light / dark):
 *   - rest:  text-ink-800 / text-ink-100
 *   - hover: bg-ink-100 + text-ink-900 / bg-ink-700 + text-ink-50
 *   - focus: same as hover (Radix sets `data-highlighted` on keyboard
 *     focus, which maps to the `focus:` pseudo-class via the
 *     primitive's outline-none transition).
 */
export const USER_MENU_ROW_CLASS =
  'text-[var(--color-ink-800)] focus:bg-[var(--color-ink-100)] focus:text-[var(--color-ink-900)] data-[highlighted]:bg-[var(--color-ink-100)] data-[highlighted]:text-[var(--color-ink-900)] dark:text-[var(--color-ink-100)] dark:focus:bg-[var(--color-ink-700)] dark:focus:text-[var(--color-ink-50)] dark:data-[highlighted]:bg-[var(--color-ink-700)] dark:data-[highlighted]:text-[var(--color-ink-50)]';

/**
 * Top-bar user menu — avatar trigger + dropdown per
 * `sustevo-handbook/brand/APP_SHELL.md` § 8.2.
 *
 * Dropdown layout (top → bottom):
 *   - Header row: avatar + full name + email (clicking opens profile)
 *   - Profile         → /settings#profile
 *   - Settings        → /settings (language lives here)
 *   - Help            → /help
 *   - (Partner switcher block, when memberships.length > 1)
 *   - ───
 *   - Sign out        ← always last
 *
 * Reads identity from `useAuth()` (Entra MSAL). Sign-out delegates
 * to `useAuth().logout()`. The partner switcher block only renders
 * when the user has multi-partner access; otherwise the dropdown
 * skips straight to the sign-out separator.
 *
 * Language picker is NOT in this menu — it lives in /settings.
 */
export function UserMenu(): JSX.Element {
  const { t } = useTranslation();
  const navigate = useNavigate();
  const { account, logout } = useAuth();
  const { active, partners, isLoading, isError } = useActivePartner();
  // W102-F3-S1: switch via the full-reset hook — clears the entire
  // React-Query cache + drops org-scoped Zustand state + navigates
  // to /dashboard so deep-link pages from the previous org don't
  // briefly render with the new tenant's headers.
  const switchOrg = useSwitchOrg();

  const account_ = account as
    | { name?: string; username?: string; email?: string }
    | null;
  const fullName = account_?.name ?? account_?.username ?? 'Guest';
  const email = account_?.email ?? account_?.username ?? '';

  const initials = fullName
    .split(/\s+/)
    .filter(Boolean)
    .slice(0, 2)
    .map((part) => part[0]?.toUpperCase() ?? '')
    .join('');

  async function handleSignOut(): Promise<void> {
    try {
      await logout();
    } catch {
      // Logout failure is non-fatal — the user can hard-refresh.
    }
  }

  const showPartnerList = !isError && !isLoading && partners.length > 1;

  return (
    <DropdownMenu>
      <DropdownMenuTrigger asChild>
        <Button
          variant="ghost"
          className="h-9 w-9 rounded-full p-0 hover:ring-2 hover:ring-[var(--color-ink-200)] dark:hover:ring-[var(--color-ink-700)]"
          aria-label={t('topbar.userMenu.aria', {
            defaultValue: 'Open user menu',
          })}
          data-testid="user-menu-trigger"
        >
          <Avatar className="h-9 w-9">
            <AvatarImage alt="" />
            <AvatarFallback className="bg-[var(--color-blue-500)] text-xs font-medium text-white">
              {initials || <User className="h-4 w-4" />}
            </AvatarFallback>
          </Avatar>
        </Button>
      </DropdownMenuTrigger>
      <DropdownMenuContent
        align="end"
        className="min-w-[14rem]"
        data-testid="user-menu-content"
      >
        <DropdownMenuLabel className="flex flex-col gap-0.5">
          <span className="max-w-[200px] truncate text-sm font-medium">
            {fullName}
          </span>
          {email ? (
            <span className="max-w-[200px] truncate text-xs text-[var(--color-ink-600)] dark:text-[var(--color-ink-400)]">
              {email}
            </span>
          ) : null}
          {/* W120-F4: always show active org indicator so single-membership
              users know which org their session is scoped to. The full
              switcher dropdown (below) still gates on partners.length > 1. */}
          {active ? (
            <span
              className="mt-1 max-w-[200px] truncate text-xs text-[var(--color-ink-600)] dark:text-[var(--color-ink-400)]"
              data-testid="user-menu-active-org"
            >
              {t('topbar.userMenu.youreIn', {
                defaultValue: "You're in: {{name}}",
                name: active.name,
              })}
            </span>
          ) : null}
        </DropdownMenuLabel>
        <DropdownMenuSeparator />
        <DropdownMenuItem
          onClick={() => navigate('/settings#profile')}
          data-testid="user-menu-profile"
          className={USER_MENU_ROW_CLASS}
        >
          {t('topbar.userMenu.profile', { defaultValue: 'Profile' })}
        </DropdownMenuItem>
        <DropdownMenuItem
          onClick={() => navigate('/settings')}
          data-testid="user-menu-settings"
          className={USER_MENU_ROW_CLASS}
        >
          {t('topbar.userMenu.settings', { defaultValue: 'Settings' })}
        </DropdownMenuItem>
        <DropdownMenuItem
          onClick={() => navigate('/help')}
          data-testid="user-menu-help"
          className={USER_MENU_ROW_CLASS}
        >
          {t('topbar.userMenu.help', { defaultValue: 'Help' })}
        </DropdownMenuItem>

        {showPartnerList ? (
          <>
            <DropdownMenuSeparator />
            <DropdownMenuLabel className="text-[10px] font-mono uppercase tracking-wider text-[var(--color-ink-600)] dark:text-[var(--color-ink-400)]">
              {t('topbar.partnerSwitcher.label', { defaultValue: 'Partner' })}
            </DropdownMenuLabel>
            {partners.map((p) => (
              <DropdownMenuItem
                key={p.id}
                onSelect={() => {
                  if (p.id !== active?.id) void switchOrg(p.id);
                }}
                className={cn('flex items-center gap-2', USER_MENU_ROW_CLASS)}
                data-testid={`partner-option-${p.slug}`}
              >
                <PartnerInitials name={p.name} />
                <span className="flex-1 truncate">{p.name}</span>
                {p.id === active?.id ? (
                  <Check
                    className="h-4 w-4 text-[var(--color-ink-600)] dark:text-[var(--color-ink-400)]"
                    aria-hidden="true"
                  />
                ) : null}
              </DropdownMenuItem>
            ))}
          </>
        ) : null}

        <DropdownMenuSeparator />
        <DropdownMenuItem
          onClick={handleSignOut}
          data-testid="user-menu-signout"
          className={USER_MENU_ROW_CLASS}
        >
          {t('topbar.userMenu.signOut', { defaultValue: 'Sign out' })}
        </DropdownMenuItem>
      </DropdownMenuContent>
    </DropdownMenu>
  );
}

function PartnerInitials({ name }: { name: string }): JSX.Element {
  const initials = name
    .split(/\s+/)
    .filter(Boolean)
    .slice(0, 2)
    .map((part) => part[0]?.toUpperCase() ?? '')
    .join('');
  return (
    <Avatar className={cn('h-5 w-5 shrink-0')}>
      <AvatarFallback className="bg-[var(--color-blue-500)] text-[10px] text-white">
        {initials || '?'}
      </AvatarFallback>
    </Avatar>
  );
}
